import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';

const CookiePolicyPage = () => {
  usePageTitle('Cookie Policy');
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to Home
          </Link>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12 text-[#211F1F]">
            <p className="text-sm text-gray-500 uppercase tracking-wider mb-2">
              YourNest Capital Advisors Pvt. Ltd.
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-[#211F1F] mb-4">
              COOKIE POLICY
            </h1>
            <p className="text-gray-600 text-sm mb-10 border-b border-gray-200 pb-6">
              Effective Date: 4 March 2026 &nbsp;|&nbsp; Last Updated: 4 March 2026
            </p>

            <div className="prose prose-gray max-w-none text-[15px] leading-relaxed space-y-6">
              <p>
                This Cookie Policy forms an integral part of and should be read together with our <Link to="/privacy-policy" className="text-[#C8473A] hover:underline font-medium">Privacy Policy</Link> and <Link to="/terms-and-conditions" className="text-[#C8473A] hover:underline font-medium">Terms &amp; Conditions</Link>.
              </p>

              <h2 className="text-xl font-bold text-[#211F1F] mt-10 mb-3">1. Introduction</h2>
              <p>
                YourNest Capital Advisors Pvt. Ltd. (&quot;YourNest&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) uses cookies and similar technologies on <a href="https://www.yournest.in" target="_blank" rel="noopener noreferrer" className="text-[#C8473A] hover:underline">www.yournest.in</a> (the &quot;Website&quot;).
              </p>
              <p>
                Under the Digital Personal Data Protection Act, 2023 (&quot;DPDP Act&quot;), YourNest acts as a Data Fiduciary in relation to personal data collected through cookies. This Cookie Policy explains:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>What cookies we use</li>
                <li>The purpose of such Processing</li>
                <li>The legal basis for Processing</li>
                <li>How you may provide, manage, or withdraw your Consent</li>
              </ul>
              <p>
                YourNest uses cookies solely for analytics purposes through Google Analytics. We do not use advertising, behavioural profiling, remarketing, or targeting cookies.
              </p>

              <h2 className="text-xl font-bold text-[#211F1F] mt-10 mb-3">2. What Are Cookies?</h2>
              <p>
                Cookies are small text files placed on your device when you access a website. Cookies may enable the collection of information that may constitute Personal Data under the DPDP Act where such information relates to an identifiable individual.
              </p>
              <p>Cookies may be:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Session Cookies</strong> – deleted when you close your browser; or</li>
                <li><strong>Persistent Cookies</strong> – stored for a defined duration.</li>
              </ul>

              <h2 className="text-xl font-bold text-[#211F1F] mt-10 mb-3">3. Categories of Cookies Used</h2>
              <p>YourNest uses only the following category:</p>

              <h3 className="text-lg font-semibold text-[#211F1F] mt-6 mb-2">3.1 Analytics Cookies (Google Analytics)</h3>
              <p>
                We use Google Analytics to collect technical and usage-related information, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP address (processed in truncated or anonymised form, where configured)</li>
                <li>Browser type and version</li>
                <li>Device type and operating system</li>
                <li>Pages visited</li>
                <li>Time spent on pages</li>
                <li>Referring URL</li>
                <li>Approximate geographic location (city/country level)</li>
                <li>Interaction metrics</li>
              </ul>
              <p>This information is:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Used exclusively to analyse Website performance and improve functionality</li>
                <li>Aggregated and de-identified for reporting purposes</li>
                <li>Not used to create user profiles</li>
                <li>Not used for targeted advertising</li>
                <li>Not used for cross-platform behavioural tracking</li>
              </ul>
              <p>
                We do not enable Google Analytics advertising features.
              </p>
              <p>
                Google may process certain technical data in accordance with its own privacy framework. You may review Google&apos;s privacy policy at: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#C8473A] hover:underline">https://policies.google.com/privacy</a>.
              </p>

              <h2 className="text-xl font-bold text-[#211F1F] mt-10 mb-3">4. Legal Basis for Processing Under the DPDP Act</h2>
              <p>
                Under Section 4 and Section 6 of the DPDP Act:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Processing of Personal Data requires free, specific, informed, unconditional, and unambiguous Consent, signified through a clear affirmative action, unless falling within a specified &quot;legitimate use.&quot;</li>
                <li>Analytics cookies do not fall within automatic &quot;legitimate use&quot; categories and are therefore processed based on Consent.</li>
              </ul>
              <p>Accordingly:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Analytics cookies are deployed only after you provide Consent, where required.</li>
                <li>No analytics cookies are activated by default prior to your consent selection (subject to technical implementation).</li>
              </ul>

              <h2 className="text-xl font-bold text-[#211F1F] mt-10 mb-3">5. Cookie Consent Mechanism</h2>
              <p>
                When you first access the Website, you will be presented with a cookie notice enabling you to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Accept analytics cookies</li>
                <li>Decline analytics cookies</li>
                <li>Manage preferences</li>
              </ul>
              <p>
                Consent is recorded through a clear affirmative action (e.g., clicking &quot;Accept&quot;).
              </p>
              <p>
                Failure to provide consent will result in analytics cookies not being deployed.
              </p>

              <h2 className="text-xl font-bold text-[#211F1F] mt-10 mb-3">6. Withdrawal of Consent</h2>
              <p>
                Under the DPDP Act, a Data Principal has the right to withdraw Consent at any time.
              </p>
              <p>You may withdraw Consent by:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Adjusting your cookie preferences via the Website (if available);</li>
                <li>Deleting or blocking cookies through your browser settings; or</li>
                <li>Installing the Google Analytics Opt-Out Browser Add-On: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#C8473A] hover:underline">https://tools.google.com/dlpage/gaoptout</a></li>
              </ul>
              <p>Withdrawal of Consent shall:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Not affect the lawfulness of Processing prior to withdrawal; and</li>
                <li>Result in cessation of further analytics cookie-based Processing.</li>
              </ul>
              <p>
                The Website will continue to function without analytics cookies.
              </p>

              <h2 className="text-xl font-bold text-[#211F1F] mt-10 mb-3">7. Retention of Cookie Data</h2>
              <p>Analytics cookie data is retained:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>For the duration configured within Google Analytics (subject to YourNest&apos;s internal retention configuration); and</li>
                <li>Only for as long as necessary to fulfil the purpose of Website performance analysis.</li>
              </ul>
              <p>
                Aggregated or anonymised statistical data may be retained for research and operational improvement purposes.
              </p>

              <h2 className="text-xl font-bold text-[#211F1F] mt-10 mb-3">8. Cross-Border Processing</h2>
              <p>
                Google Analytics may involve processing of certain technical data outside India.
              </p>
              <p>In accordance with the DPDP Act:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Cross-border transfer is permitted unless restricted by notification of the Central Government; and</li>
                <li>YourNest ensures that such Processing occurs in compliance with applicable law and contractual safeguards.</li>
              </ul>

              <h2 className="text-xl font-bold text-[#211F1F] mt-10 mb-3">9. Data Principal Rights</h2>
              <p>
                To the extent cookie-related information constitutes Personal Data under the DPDP Act, you have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Request a summary of Personal Data processed</li>
                <li>Seek correction of inaccurate data</li>
                <li>Request erasure, subject to lawful retention requirements</li>
                <li>Withdraw Consent</li>
                <li>File a grievance with the Grievance Officer</li>
              </ul>
              <p>For such requests, please contact:</p>
              <p className="font-semibold mt-2">Grievance Officer</p>
              <p>YourNest Capital Advisors Pvt. Ltd.</p>
              <p>416, 4th Floor, MGF Metropolis Mall</p>
              <p>Near M.G. Road Metro Station</p>
              <p>Gurugram – 122002, India</p>
              <p>Email: <a href="mailto:sanjay.jha@yournest.in" className="text-[#C8473A] hover:underline">sanjay.jha@yournest.in</a></p>
              <p>
                If unsatisfied with the response, you may approach the Data Protection Board of India in accordance with the DPDP Act.
              </p>

              <h2 className="text-xl font-bold text-[#211F1F] mt-10 mb-3">10. Changes to This Cookie Policy</h2>
              <p>
                We may update this Cookie Policy to reflect changes in:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Applicable law</li>
                <li>Google Analytics configuration</li>
                <li>Website functionality</li>
              </ul>
              <p>
                Any updates will be published on this page with a revised &quot;Last Updated&quot; date.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
